import { useState } from "react";
import { AlertCircle, Clock, Loader2 } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { toast } from "sonner";

export function SubscriptionStatusBanner() {
  const { subscription, loading } = useSubscription();
  const [opening, setOpening] = useState(false);

  if (loading || !subscription) return null;

  const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
  const daysLeft = periodEnd ? Math.ceil((periodEnd.getTime() - Date.now()) / 86_400_000) : null;

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data, error } = await supabase.functions.invoke("paddle-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || !data?.url) throw new Error(error?.message ?? "Could not open portal");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOpening(false);
    }
  };

  const PortalBtn = ({ children }: { children: React.ReactNode }) => (
    <button onClick={openPortal} disabled={opening} className="underline font-medium inline-flex items-center gap-1">
      {opening && <Loader2 className="h-3 w-3 animate-spin" />}
      {children}
    </button>
  );

  if (subscription.status === "past_due") {
    return (
      <Banner tone="danger" icon={<AlertCircle className="h-4 w-4" />}>
        Your last payment failed. Update your payment method to keep access.{" "}
        <PortalBtn>Update payment method</PortalBtn>
      </Banner>
    );
  }
  if (subscription.status === "trialing" && daysLeft !== null && daysLeft <= 2) {
    return (
      <Banner tone="warning" icon={<Clock className="h-4 w-4" />}>
        Your free trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}.{" "}
        <PortalBtn>Manage plan</PortalBtn>
      </Banner>
    );
  }
  if (subscription.cancel_at_period_end && daysLeft !== null && daysLeft >= 0) {
    return (
      <Banner tone="warning" icon={<Clock className="h-4 w-4" />}>
        Your subscription ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}.{" "}
        <PortalBtn>Resume in billing portal</PortalBtn>
      </Banner>
    );
  }
  return null;
}

function Banner({ tone, icon, children }: { tone: "warning" | "danger"; icon: React.ReactNode; children: React.ReactNode }) {
  const cls = tone === "danger"
    ? "bg-red-100 border-red-300 text-red-900"
    : "bg-amber-100 border-amber-300 text-amber-900";
  return (
    <div className={`w-full border-b px-4 py-2 text-center text-sm flex items-center justify-center gap-2 ${cls}`}>
      {icon}
      <span>{children}</span>
    </div>
  );
}
